import { Anchor, Badge, Group, SimpleGrid, Stack, Table, Text } from "@mantine/core";

import type {
  OutputStatus,
  TraceFile,
  TraceInputs as Inputs,
  TraceOutput,
  TraceSource,
} from "../../api/generated/model";
import { CardPicture } from "../../ui/CardPicture";
import { formatBytes } from "../../ui/format";
import { Facts, formatDuration, formatUsd } from "./usage-format";
import classes from "./usage.module.css";

/** A file of the trace by its storage key: its own object's link while it lasts; none once gone. */
export type FileOf = (key: string | null) => TraceFile | undefined;

const KIND: Record<TraceSource["kind"], string> = {
  job_output: "A job's output",
  asset: "A library file",
  actor: "An actor",
  global_actor: "A global actor",
  example: "A template's example",
  voice: "A voice's sample",
};

/** What a file is: its type, size, shape and length, each where known. */
function factsOf(file: {
  mime?: string | null;
  bytes?: number | null;
  width?: number | null;
  height?: number | null;
  duration_ms?: number | null;
}): string {
  const { mime, bytes, width, height, duration_ms: length } = file;
  const shape = width == null || height == null ? null : `${width} × ${height}`;
  const parts = [mime, bytes == null ? null : formatBytes(bytes), shape];
  return [...parts, length == null ? null : formatDuration(length)].filter(Boolean).join(" · ");
}

/**
 * A file's preview, which opens the file whole in a tab through its own 15-minute link: a
 * picture's thumbnail (which stays mounted if it fails, as every picture does, under "link
 * expired") or, for another file, "Open"; "gone" once the bucket no longer has it.
 */
function Preview({ file, label }: { file: TraceFile | undefined; label: string }) {
  if (file === undefined) {
    return (
      <span className={classes.thumb} data-gone="">
        gone
      </span>
    );
  }
  const open = { href: file.url, target: "_blank", rel: "noreferrer", "aria-label": `Open ${label}` };
  if (!file.content_type.startsWith("image/")) {
    return (
      <Anchor {...open} size="sm">
        Open
      </Anchor>
    );
  }
  return (
    <Anchor {...open} className={classes.thumb} underline="never">
      <CardPicture
        src={file.url}
        alt={label}
        fallback={<span className={classes.lapsed}>link expired</span>}
      />
    </Anchor>
  );
}

const settingText = (value: unknown) =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : JSON.stringify(value);

/**
 * A job's inputs (spec usage-analytics 3): its prompt, its settings as its type reads them and
 * each file its snapshot names, beside the snapshot as stored; "expired" once retention cleared it.
 */
export function TraceInputs({ inputs, fileOf }: { inputs: Inputs | null; fileOf: FileOf }) {
  if (inputs === null) {
    return (
      <Group gap="xs">
        <Badge color="gray" variant="light">
          expired
        </Badge>
        <Text size="sm" c="var(--cai-color-text-muted)">
          Retention cleared this job's inputs; the facts on this page stay.
        </Text>
      </Group>
    );
  }
  const settings = Object.entries(inputs.settings ?? {}).filter(
    ([name, value]) => name !== "prompt" && value !== null && value !== undefined,
  );
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Text size="xs" c="var(--cai-color-text-muted)">
            Prompt
          </Text>
          {inputs.prompt === null ? (
            <Text size="sm">None</Text>
          ) : (
            <Text className={classes.prompt}>{inputs.prompt}</Text>
          )}
        </Stack>
        {settings.length === 0 ? null : (
          <Facts items={settings.map(([name, value]) => [name.replaceAll("_", " "), settingText(value)])} />
        )}
        <Stack gap={6}>
          <Text size="xs" c="var(--cai-color-text-muted)">
            Sources
          </Text>
          {inputs.sources.length === 0 ? <Text size="sm">None</Text> : null}
          {inputs.sources.map((source) => (
            <Group key={source.loc.join(".")} gap="sm" wrap="nowrap" className={classes.source}>
              <Preview file={fileOf(source.storage_key)} label={`source at ${source.loc.join(".")}`} />
              <Stack gap={2} miw={0}>
                <Text size="sm">
                  {KIND[source.kind]} · {factsOf(source)}
                </Text>
                <Text size="xs" ff="monospace" style={{ overflowWrap: "anywhere" }}>
                  {source.storage_key}
                </Text>
                <Text size="xs" c="var(--cai-color-text-muted)">
                  At {source.loc.join(".")} in the snapshot
                </Text>
              </Stack>
            </Group>
          ))}
        </Stack>
      </Stack>
      <details open>
        <Text component="summary" size="sm" c="var(--cai-color-text-link)">
          The snapshot as stored
        </Text>
        <pre className={classes.json}>{JSON.stringify(inputs.snapshot, null, 2)}</pre>
      </details>
    </SimpleGrid>
  );
}

const OUTPUT: Record<OutputStatus, { label: string; color?: string }> = {
  pending: { label: "Pending" },
  delivered: { label: "Delivered", color: "teal" },
  failed: { label: "Failed", color: "red" },
};

/** A job's outputs as their rows keep them, each file through its own link while it lasts. */
export function TraceOutputs({ outputs, fileOf }: { outputs: TraceOutput[]; fileOf: FileOf }) {
  if (outputs.length === 0) return <Text size="sm">None.</Text>;
  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={960}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.num}>#</Table.Th>
              <Table.Th>Preview</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Key</Table.Th>
              <Table.Th>File</Table.Th>
              <Table.Th>SKU usage</Table.Th>
              <Table.Th className={classes.num}>Vendor cost</Table.Th>
              <Table.Th>Saved asset</Table.Th>
              <Table.Th>Error</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {outputs.map((output) => {
              const { label, color } = OUTPUT[output.status];
              return (
                <Table.Tr key={output.index}>
                  <Table.Td className={classes.num}>{output.index}</Table.Td>
                  <Table.Td>
                    {output.storage_key === null ? (
                      <span className={classes.zero}>—</span>
                    ) : (
                      <Preview file={fileOf(output.storage_key)} label={`output ${output.index}`} />
                    )}
                  </Table.Td>
                  <Table.Td className={classes.status}>
                    <Badge variant="light" {...(color === undefined ? {} : { color })}>
                      {label}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" style={{ overflowWrap: "anywhere" }}>
                      {output.storage_key ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{factsOf(output) || "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    {output.usage.map((unit) => (
                      <Text key={unit.sku} size="xs" ff="monospace" className={classes.nowrap}>
                        {`${unit.sku}: ${unit.quantity}`}
                      </Text>
                    ))}
                  </Table.Td>
                  <Table.Td className={classes.num}>
                    {output.vendor_cost_usd === null ? "—" : formatUsd(output.vendor_cost_usd, true)}
                  </Table.Td>
                  <Table.Td>
                    {output.saved_asset_id === null ? (
                      <span className={classes.zero}>—</span>
                    ) : (
                      <Text size="xs" ff="monospace">
                        {output.saved_asset_id}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {output.error_code === null ? (
                      <span className={classes.zero}>—</span>
                    ) : (
                      <Text size="xs" ff="monospace">
                        {output.error_code}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Text size="xs" c="var(--cai-color-text-muted)">
        An output's file lives 30 days unless saved to the library. Each preview opens the file
        whole, through a link to that one file, never the team's prefix or the media cookie.
      </Text>
    </Stack>
  );
}
